import React, { useRef, useState } from "react"
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Box,
  Flex,
  Button,
  Input,
  VStack,
  Text,
  Heading,
} from "@chakra-ui/react"
import axios from "axios"

import { useCore } from "@app/hooks/useCore"
import { useConfigContext } from "@app/providers/config"
import { useBackInStock } from "@app/hooks/useBackInStock"
import { useMedia } from "@app/hooks/useMedia"

type Props = {
  isOpen: boolean
  onClose: () => void
  product: any
}

const ProductBackInStock: React.FC<Props> = ({ isOpen, onClose, product }) => {
  const {
    helpers: { decodeShopifyId },
  } = useCore()
  const { store } = useConfigContext()
  const { isMedium } = useMedia()
  const initialRef = useRef()
  const { title1, title2, content1, subContent1, content2, button1, emailPlaceholder } = useBackInStock(product)
  const [email, setEmail] = useState("")
  const [sent, setSent] = useState(false)
  const [loading, setIsLoading] = useState(false)

  const isValidEmail = (email: string) => {
    return email?.length > 3 && /\S+@\S+\.\S+/.test(email)
  }

  const onSubmit = async (e: any) => {
    e.preventDefault()

    if (isValidEmail(email)) {
      setIsLoading(true)
      const body = {
        store: store.shopifyShopDomain,
        email,
        product: decodeShopifyId(product?.id, "Product"),
        url: product?.url,
        variant: decodeShopifyId(product?.variants[0]?.id, "ProductVariant"),
        ajax: true,
      }

      const url = "https://alertmerestockalerts.herokuapp.com/addemail"

      try {
        await axios.post(url, body)
        setSent(true)
      } catch (error: any) {
        console.error(error)
      }

      setIsLoading(false)
    }
  }

  return (
    <Modal onClose={onClose} isOpen={isOpen} initialFocusRef={initialRef} motionPreset="slideInBottom" isCentered size="4xl">
      <ModalOverlay />
      <ModalContent
        mx="25px"
        maxWidth="442px"
        borderWidth="10px"
        borderStyle="solid"
        sx={{
          borderImage: "linear-gradient(#F9423A, #FFAE61, #A5E065, #EAFBFA, #77A5FF, #BDD6E6, #EAFBFA, #A5E065) 1%",
          borderImageSlice: "50",
        }}
      >
        <Flex direction="column" alignSelf="center" height="100%" padding={{ base: "24px", md: "40px" }}>
          <ModalHeader padding="0">
            <Box display="flex" alignItems="center" justifyContent="center">
              {!sent ? (
                <>
                  <Heading as="h1" fontSize="23px" fontWeight="700" marginBottom="8px">
                    {title1}
                  </Heading>
                </>
              ) : (
                <Heading as="h1" pt="50px">
                  {title2}
                </Heading>
              )}
            </Box>
          </ModalHeader>

          <ModalCloseButton marginTop="5px" />

          <ModalBody textAlign="center" paddingTop="0" paddingX="0">
            {!sent ? (
              <>
                {content1.map((text, idx) => (
                  <Text
                    key={`content-${idx}`}
                    variant={isMedium ? "paragraph16" : "paragraph14"}
                    fontFamily="Larsseit"
                    mb="24px"
                    lineHeight="20px"
                    marginTop={idx === 0 && "0"}
                  >
                    {text}
                  </Text>
                ))}
              </>
            ) : (
              <>
                {content2.map((text, idx) => (
                  <Text key={`content-${idx}`} variant={idx > 0 ? "paragraph14" : "paragraph16"} lineHeight="20px">
                    {text}
                  </Text>
                ))}
              </>
            )}
            <Flex as="form" direction="column" onSubmit={onSubmit}>
              <VStack spacing="4">
                {!sent && (
                  <>
                    <Text alignSelf="flex-start" lineHeight="0" variant="paragraph12" fontWeight="500">
                      {emailPlaceholder}
                    </Text>

                    <Input
                      marginTop="0 !important"
                      variant="outline"
                      name="email"
                      type="email"
                      borderColor="brand.darkBlue"
                      borderRadius="8px"
                      fontSize={["14px", "16px"]}
                      placeholder={isMedium ? "Your Email here" : "Email"}
                      onChange={e => setEmail(e.target.value)}
                      value={email}
                    />
                    {subContent1.map((text, idx) => (
                      <Text key={`content-${idx}`} variant="paragraph12" lineHeight="20px" fontWeight="400">
                        {text}
                      </Text>
                    ))}
                  </>
                )}

                {!sent && (
                  <Button variant="fill" px="75px" width="100%" type="submit" isDisabled={loading || email.length < 3} isLoading={loading}>
                    {button1}
                  </Button>
                )}
              </VStack>
            </Flex>
          </ModalBody>
        </Flex>
      </ModalContent>
    </Modal>
  )
}

export default React.memo(ProductBackInStock)
